import DateValue from "@/components/tableElements/DateValue.vue";
import { coalesceNumber } from "@/utils/transformers";
import moment from "moment";
import { mapGetters } from "vuex";
import { WEEK_SWITCH } from "@/utils/datepicker";
export default {
  name: "SimpleAreaChart",
  components: {
    DateValue
  },
  props: {
    data: {
      type: Array,
      default: () => [],
      required: true
    },
    width: {
      type: Number,
      default: 300
    },
    height: {
      type: Number,
      default: 100
    },
    max: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    min: {
      type: Number,
      default: 0
    },
    popoverPosition: {
      type: String,
      default: "bottom-right",
      validator: val => ["top-left", "top-right", "bottom-left", "bottom-right"].includes(val)
    },
    highlightRange: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      tooltip: null
    };
  },
  computed: {
    ...mapGetters(["getSystemWeekType"]),
    points() {
      return this.data.map((d, i) => {
        const x = i * this.width / (this.data.length - 1);
        const y = coalesceNumber(d.value, this.max) / this.max * this.height;
        return {
          x,
          y,
          label: d.label,
          value: d.value
        };
      });
    },
    linePath() {
      return this.getValidSegments().map(segment => segment.map((p, i) => i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`).join(" ")).join(" ");
    },
    areaPath() {
      return this.getValidSegments().map(segment => {
        const first = segment[0];
        const last = segment[segment.length - 1];
        const base = segment.map((p, i) => i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`).join(" ");
        return `${base} L ${last.x} ${this.height} L ${first.x} ${this.height} Z`;
      }).join(" ");
    },
    indicator() {
      const {
        x,
        y
      } = this.tooltip || {};
      return {
        x,
        y
      };
    },
    popoverStyle() {
      if (!this.tooltip) return {};
      const offset = 2;
      const popover = this.$refs.popover;
      const width = popover?.offsetWidth || 120;
      const height = popover?.offsetHeight || 60;
      let left = this.tooltip.left;
      let top = this.tooltip.top;
      switch (this.popoverPosition) {
        case "top-left":
          left -= width + offset;
          top -= height + offset;
          break;
        case "top-right":
          left += offset;
          top -= height + offset;
          break;
        case "bottom-left":
          left -= width + offset;
          top += offset;
          break;
        case "bottom-right":
          left += offset;
          top += offset;
          break;
      }
      return {
        position: "absolute",
        left: `${left}px`,
        top: `${top}px`,
        pointerEvents: "none"
      };
    },
    highlightedSegments() {
      if (!this.highlightRange) return [];
      const start = moment(this.highlightRange.start);
      const end = moment(this.highlightRange.end);
      return this.points.filter(p => {
        const moment_label = moment(p.label);
        const label_start = moment_label.clone();
        const label_end = moment.min(moment().subtract(1, 'day'), label_start.clone().add(6, 'days'));
        const match = start.isSameOrBefore(label_start, 'day') && end.isSameOrAfter(label_end, 'day');
        return match;
      });
    },
    highlightAreaPath() {
      const points = this.highlightedSegments;
      if (!points || points.length <= 1) return null;
      const basePath = points.map((p, i) => i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`).join(" ");
      return basePath;
    },
    highlightTooltipPoints() {
      const pts = this.highlightedSegments || [];
      return {
        start: pts.length > 0 ? pts[0] : null,
        end: pts.length > 1 ? pts[pts.length - 1] : null
      };
    }
  },
  methods: {
    coalesceNumber,
    getValidSegments() {
      const segments = [];
      let current = [];
      for (const point of this.points) {
        if (point.value == null) {
          if (current.length) {
            segments.push([...current]);
            current = [];
          }
        } else {
          current.push(point);
        }
      }
      if (current.length) {
        segments.push(current);
      }
      return segments;
    },
    handleMouseMove(event) {
      if (!this.points.length) return;
      const svg = this.$refs.chart;
      if (!svg) return;
      const pt = svg.createSVGPoint();
      pt.x = event.clientX;
      pt.y = event.clientY;
      const cursorpt = pt.matrixTransform(svg.getScreenCTM().inverse());
      if (!cursorpt) return;
      const closest = this.points.reduce((acc, p) => {
        const dist = Math.abs(p.x - cursorpt.x);
        return dist < acc.dist ? {
          dist,
          point: p
        } : acc;
      }, {
        dist: Infinity,
        point: null
      }).point;
      if (!closest) return;
      const svgPoint = svg.createSVGPoint();
      svgPoint.x = closest.x;
      svgPoint.y = closest.y;
      const screenPoint = svgPoint.matrixTransform(svg.getScreenCTM());
      const svgRect = svg.getBoundingClientRect();
      this.tooltip = {
        ...closest,
        left: screenPoint.x - svgRect.left,
        top: screenPoint.y - svgRect.top
      };
      this.$nextTick(() => {
        // Forces popover to update with correct dimensions
        this.$forceUpdate();
      });
    },
    handleMouseOut() {
      this.tooltip = null;
    }
  }
};